import { AfterViewChecked, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Location } from "../../../../_objects/report-element";
import { Notifier, OfflineReportPreviewService } from "../../../../_services/offline-report-preview.service";
import { ReportPage } from "../../../models/report-preview/report-page";
import { OfflineUtilsService } from "../../../services/offline-utils.service";

@Component({
  selector: "app-report-page-container-offline",
  templateUrl: "./report-page.component.html",
  styleUrls: ["./report-page.component.scss"],
  standalone: false
})
export class ReportPageOfflineComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() public pageKey: string;
  @Input() public isLastPage: boolean;

  public reportPage: ReportPage = null;
  public fontFamily: string;
  public fontSize: string;

  public headerTextKeys: Array<string>;
  public footerTextKeys: Array<string>;
  public bodyTextKeys: Array<string>;

  public headerLineKeys: Array<string>;
  public footerLineKeys: Array<string>;
  public bodyLineKeys: Array<string>;

  public headerImageKeys: Array<string>;
  public footerImageKeys: Array<string>;
  public bodyImageKeys: Array<string>;

  public headerRectangleKeys: Array<string>;
  public footerRectangleKeys: Array<string>;
  public bodyRectangleKeys: Array<string>;

  public headerSummaryKeys: Array<string>;
  public footerSummaryKeys: Array<string>;
  public bodySummaryKeys: Array<string>;

  public headerPictureKeys: Array<string>;
  public footerPictureKeys: Array<string>;
  public bodyPictureKeys: Array<string>;

  public headerDataPlateKeys: Array<string>;
  public footerDataPlateKeys: Array<string>;
  public bodyDataPlateKeys: Array<string>;

  public headerTableKeys: Array<string>;
  public footerTableKeys: Array<string>;
  public bodyTableKeys: Array<string>;

  public headerMeasurementPointKey: Array<string>;
  public footerMeasurementPointKey: Array<string>;
  public bodyMeasurementPointKey: Array<string>;

  public headerVisualEventsKeys: Array<string>;
  public footerVisualEventsKeys: Array<string>;
  public bodyVisualEventsKeys: Array<string>;

  public headerSignatureKeys: Array<string>;
  public footerSignatureKeys: Array<string>;
  public bodySignatureKeys: Array<string>;

  public bodyInteractiveIsoKeys: Array<string>;

  public bodyDropDownListKeys: Array<string>;
  public bodyCustomElementsKeys: Array<string>;

  public sectionKeys: Array<string>;

  private notifier: Notifier;

  public cmRuler: number[];
  public mmRuler: number[];
  public cmSideRuler: number[];
  public sideRulerLeftMargin: string;
  public sideRulerTopMargin: string;
  public readonly defaultRuler = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; // creating default array for ngFor in ruler
  private onDestroy$ = new Subject();

  readonly containerStyle = "display: flex; flex-direction: column; align-items: center; box-sizing: border-box;";
  readonly reportPageStyle = "position: relative; margin: 0 auto; background-color: #FFF;";
  readonly headerStyle = "width: 100%; position: relative; overflow: hidden; box-sizing: border-box;";
  readonly bodyStyle = "width: 100%; position: relative; overflow: hidden; box-sizing: border-box;";
  readonly footerStyle = "width: 100%; position: relative; overflow: hidden; box-sizing: border-box;";
  readonly ghostLayerStyle = "position: absolute; z-index: 2;";
  readonly previewLayerStyle = "z-index: 2;";
  readonly componentOuterStyle = "display: block; position: absolute;";

  isLoaded = false;
  constructor(
    private router: Router,
    public designService: OfflineReportPreviewService,
    private offlineUtilsService: OfflineUtilsService
  ) {
    this.mmRuler = this.defaultRuler;
    this.cmRuler = this.defaultRuler;
    this.cmSideRuler = this.defaultRuler;
  }

  get isSaveHtml() {
    return this.designService.isSaveHtml;
  }

  get isPreview() {
    return this.designService.isPreview;
  }

  // This component only ever displays non-completed (draft) reports on screen — completed reports
  // are shown as their generated PDF — so the watermark is always visible live. During HTML
  // serialization it must appear in the draft snapshot (isPreview) but never in the final report.
  get showDraftWatermark() {
    return this.isSaveHtml ? this.isPreview : true;
  }

  get pageStyle() {
    if (!this.isSaveHtml) {
      return "border: 1px solid #ccc; margin: 0 auto; margin-top: 0; margin-bottom: 10px;";
    }

    let str = "background-color: none; page-break-after: always; margin: 0; border: none; overflow: hidden;";
    if (this.isLastPage) {
      str += " page-break-after: avoid;";
    }
    return str;
  }

  public async ngOnInit() {
    this.offlineUtilsService
      .getAppDirectory()
      .pipe(takeUntil(this.onDestroy$))
      .toPromise()
      .then(() => {
        this.notifier = {
          componentName: "report-page.component",
          ids: { pageId: this.pageKey, elementId: "" }
        };

        this.reportPage = this.designService.getPreviewPage(this.pageKey);
        this.fontFamily = this.designService.getTemplateFontFamily();
        this.fontSize = this.designService.getTemplateFontSize().toString() + "mm";
        this.sideRulerLeftMargin = -(parseInt(this.reportPage.paddingLeft, 10) + 4.75) + "mm"; // 4.75 is the default value
        this.sideRulerTopMargin = -(parseInt(this.reportPage.paddingTop, 10) + parseInt(this.reportPage.headerHeight, 10)) + "mm";
        this.adjustRulerHeight(this.reportPage.paperSize.getPaperSize());

        this.headerTextKeys = this.designService.getTextIds(this.pageKey, "Header");
        this.headerLineKeys = this.designService.getLineIds(this.pageKey, "Header");
        this.headerImageKeys = this.designService.getImageIds(this.pageKey, "Header");
        this.headerRectangleKeys = this.designService.getRectangleIds(this.pageKey, "Header");
        this.headerSummaryKeys = this.designService.getSummaryIds(this.pageKey, "Header");
        this.headerPictureKeys = this.designService.getPictureIds(this.pageKey, "Header");
        this.headerDataPlateKeys = this.designService.getDataPlateIds(this.pageKey, "Header");
        this.headerMeasurementPointKey = this.designService.getMeasurementPointIds(this.pageKey, "Header");
        this.headerVisualEventsKeys = this.designService.getVisualEventsIds(this.pageKey, "Header");
        this.headerSignatureKeys = this.designService.getSignatureIds(this.pageKey, "Header");
        this.headerTableKeys = this.designService.getTableIds(this.pageKey, "Header");

        this.bodyTextKeys = this.designService.getTextIds(this.pageKey, "Body");
        this.bodyLineKeys = this.designService.getLineIds(this.pageKey, "Body");
        this.bodyImageKeys = this.designService.getImageIds(this.pageKey, "Body");
        this.bodyRectangleKeys = this.designService.getRectangleIds(this.pageKey, "Body");
        this.bodySummaryKeys = this.designService.getSummaryIds(this.pageKey, "Body");
        this.bodyPictureKeys = this.designService.getPictureIds(this.pageKey, "Body");
        this.bodyDataPlateKeys = this.designService.getDataPlateIds(this.pageKey, "Body");
        this.bodyMeasurementPointKey = this.designService.getMeasurementPointIds(this.pageKey, "Body");
        this.bodyVisualEventsKeys = this.designService.getVisualEventsIds(this.pageKey, "Body");
        this.bodySignatureKeys = this.designService.getSignatureIds(this.pageKey, "Body");
        this.bodyTableKeys = this.designService.getTableIds(this.pageKey, "Body");
        this.bodyInteractiveIsoKeys = this.designService.getInteractiveIsoIds(this.pageKey, "Body");
        this.bodyDropDownListKeys = this.designService.getDropDownListIds(this.pageKey, "Body");
        this.bodyCustomElementsKeys = this.designService.getCustomElementIds(this.pageKey, "Body");

        this.footerTextKeys = this.designService.getTextIds(this.pageKey, "Footer");
        this.footerLineKeys = this.designService.getLineIds(this.pageKey, "Footer");
        this.footerImageKeys = this.designService.getImageIds(this.pageKey, "Footer");
        this.footerRectangleKeys = this.designService.getRectangleIds(this.pageKey, "Footer");
        this.footerSummaryKeys = this.designService.getSummaryIds(this.pageKey, "Footer");
        this.footerPictureKeys = this.designService.getPictureIds(this.pageKey, "Footer");
        this.footerDataPlateKeys = this.designService.getDataPlateIds(this.pageKey, "Footer");
        this.footerMeasurementPointKey = this.designService.getMeasurementPointIds(this.pageKey, "Footer");
        this.footerVisualEventsKeys = this.designService.getVisualEventsIds(this.pageKey, "Footer");
        this.footerSignatureKeys = this.designService.getSignatureIds(this.pageKey, "Footer");
        this.footerTableKeys = this.designService.getTableIds(this.pageKey, "Footer");

        this.sectionKeys = this.designService.getSectionIds(this.pageKey, "Body");
        this.designService.onEndDraw.subscribe(() => {
          this.designService.alignElementsWithSectionOnPage(this.pageKey);
        });
      });
  }

  public ngAfterViewChecked() {
    if (!this.isLoaded && this.reportPage && !this.reportPage.isExisting()) {
      this.router.navigate([], { fragment: this.reportPage.getAnchorId() });
      document.querySelector("#" + this.reportPage.getAnchorId()).scrollIntoView();
      this.isLoaded = true;
    }
  }
  public ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  public adjustRulerHeight(paperSize) {
    switch (paperSize) {
      case "Letter":
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13];
        break;
      case "Legal":
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14, 15, 16];
        break;
      case "A4":
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14];
        break;
      case "B5":
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14];
        break;
    }
  }

  public getCanvasWidth(): number {
    return this.reportPage.getWidthPx(Location.Body);
  }

  public getCanvasHeight(locStr: string): number {
    const location: Location = Location.cast(locStr);
    return this.reportPage.getHeightPx(location);
  }

  public onClick(event: MouseEvent) {}

  public getDraftImage() {
    // The saved snapshot references a copy of draft.png written next to the HTML file; the live
    // preview loads the bundled asset through the app's base URL, which resolves on every platform.
    return this.isSaveHtml ? "draft.png" : "assets/images/draft.png";
  }

  get draftImageStyle() {
    const width = this.getBodyWidth();
    const height = this.getBodyHeight();

    return width <= height ? "width: 100%" : "height: 100%";
  }

  getBodyWidth() {
    const template = this.designService.originTemplate.template;
    const marginRight = template.pageMarginRight ?? 0;
    const marginLeft = template.pageMarginLeft ?? 0;
    return this.getPageWidth(template.paperSize) - (marginLeft + marginRight);
  }

  getBodyHeight() {
    const template = this.designService.originTemplate.template;
    const marginTop = template.pageMarginTop ?? 0;
    const marginBottom = template.pageMarginBottom ?? 0;
    return this.getPageHeight(template.paperSize) - (marginTop + marginBottom);
  }

  getPageWidth(paperSize: string) {
    switch (paperSize) {
      case "A4":
        return 210;
      case "B5":
        return 176;
      case "B5JIS":
        return 182;
      case "Letter":
      case "Legal":
      default:
        return 215.9;
    }
  }

  getPageHeight(paperSize: string) {
    switch (paperSize) {
      case "A4":
        return 297;
      case "B5":
        return 250;
      case "B5JIS":
        return 257;
      case "Legal":
        return 355.6;
      case "Letter":
      default:
        return 279.4;
    }
  }

  getPageMaxZIndex() {
    return Math.max(
      ...Array.from(document.querySelectorAll("body *"), (el) => parseFloat(window.getComputedStyle(el).zIndex)).filter(
        (zIndex) => !Number.isNaN(zIndex)
      ),
      0
    );
  }
}
